import React, {useEffect, useState} from 'react'
import {PageTitle} from 'layout/master-layout'
import {ITab, KTTab} from 'helpers/ui-components/KTTab'
import {useTranslation} from 'react-i18next'
import RegisterNewUser from './RegisterNewUser'
import Spinner from 'layout/common/Spinner'
import {useAuth} from 'context/AuthProvider'
import PendingUserAction from './PendingUserAction'
import Approvals from './Approvals'
import Activate from './Activate'
import RejectedUser from './RejectedUser'
import {ActivateUser} from './activate-user/ActivateUser'
import {IUserAction} from 'models/UserManagement'
import {Button} from 'helpers'
import {
 useNavigate,
 useSearchParams,
} from 'react-router-dom'
import {UserAction} from './UserAction'

type IProps = {
 hidePartent: boolean
 setHideParent: (newState: boolean) => void
}

const UserManagementPage = (props: IProps) => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [searchParams] = useSearchParams()
 const {hidePartent, setHideParent} = props
 const {userData} = useAuth()
 const accessDetails =
  userData?.userSubModule?.userManagement
 const [loader, setLoader] = useState(false)
 const [tabList] = useState<ITab[]>([
  {
   title: t('userManagement.tabs.createUser'),
   id: 'create_user',
   icon: '',
  },
  {
   title: t('userManagement.tabs.pendingUserAction'),
   id: 'pending_user_action',
   icon: '',
  },
  {
   title: t('userManagement.tabs.approvals'),
   id: 'approvals',
   icon: '',
  },
  {
   title: t('userManagement.tabs.activate'),
   id: 'activate',
   icon: '',
  },
  {
   title: t('userManagement.tabs.rejected'),
   id: 'rejected_user',
   icon: '',
  },
 ])
 const userId = ''
 const tabFromQuery = searchParams.get('tab') ?? ''
 const activeTab = tabList.find(
  (tab) => tab.id === tabFromQuery
 )
  ? tabFromQuery
  : 'create_user'
 const actionUserId = searchParams.get('userId')
 const actionType = searchParams.get('actionType')
 const hasActionDetails = Boolean(
  actionUserId && actionType
 )
 const actionDetails =
  actionUserId && actionType
   ? {
      userId: actionUserId,
      actionType,
     }
   : null

 const getUserManagementPath = (
  tabId = 'create_user',
  details?: IUserAction | null
 ) => {
  const params = new URLSearchParams({tab: tabId})
  if (details) {
   params.set('userId', details.userId)
   params.set('actionType', details.actionType)
  }
  return `/user-management?${params.toString()}`
 }

 const returnToUserManagement = () => {
  navigate(getUserManagementPath('create_user'))
 }

 const returnToTab = (tabId: string) => {
  navigate(getUserManagementPath(tabId))
 }

 const handleTabClick = (index: string) => {
  navigate(getUserManagementPath(index))
 }

 const handleActionDetailsChange = (
  newState: IUserAction | null
 ) => {
  if (newState) {
   navigate(getUserManagementPath(activeTab, newState))
  } else {
   navigate(getUserManagementPath(activeTab))
  }
 }

 useEffect(() => {
  setHideParent(hasActionDetails)
 }, [hasActionDetails, setHideParent])

 return (
  <>
   {loader && <Spinner />}
   {!actionDetails && (
    <div className='flex items-center justify-between text-[#171B1F] text-xl font-bold border-[#E6EBF0] border-b py-6'>
     <div></div>
     <div className='flex items-center '>
      {accessDetails?.manageUser && (
       <Button
        type='button'
        disabled={false}
        onClick={() =>
         navigate('/user-management/manage-users')
        }
        color='blue'
        className='text-center py-[9px] px-6 bg-[#4E83E4] hover:bg-white hover:text-[#4E83E4]
                     !rounded-lg text-base text-white font-semibold border-[#4E83E4]'
       >
        {t('userManagement.manageUser.btnText')}
       </Button>
      )}
     </div>
    </div>
   )}
   {actionDetails &&
    (actionDetails.actionType === 'activate_user' ? (
     <ActivateUser
      userId={actionDetails?.userId}
      setActionDetails={handleActionDetailsChange}
      onReturnToUserManagement={returnToUserManagement}
      onReturnToTab={returnToTab}
     />
    ) : (
     <UserAction
      actionDetails={actionDetails}
      setActionDetails={handleActionDetailsChange}
      tabList={tabList}
      onReturnToUserManagement={returnToUserManagement}
      onReturnToTab={returnToTab}
     />
    ))}
   <div
    className={`${
     hidePartent ? 'hidden' : 'visible'
    } w-full min-h-full`}
   >
    <KTTab
     activeTabId={activeTab}
     controlledTab={true}
     disableHash={true}
     containerClass='my-6 '
     tabHeaderContainerClass='mb-6 !justify-between'
     tabHeaderClass='px-[20px]'
     activeClass='text-[#4E83E4] font-semibold border-b-[3px] border-[#4E83E4] '
     onClick={handleTabClick}
     tabs={tabList}
     tabContent={
      <div>
       {activeTab === 'create_user' ? (
        <RegisterNewUser
         accessDetails={accessDetails}
         userId={userId}
         loggedInUserId={userData?.userId}
         setLoader={setLoader}
        />
       ) : activeTab === 'pending_user_action' ? (
        <PendingUserAction
         accessDetails={accessDetails}
         loggedInUserId={userData?.userId}
         actionDetails={actionDetails}
         setLoader={setLoader}
         setActionDetails={handleActionDetailsChange}
        />
       ) : activeTab === 'approvals' ? (
        <Approvals
         accessDetails={accessDetails}
         loggedInUserId={userData?.userId}
         actionDetails={actionDetails}
         setLoader={setLoader}
         setActionDetails={handleActionDetailsChange}
        />
       ) : activeTab === 'activate' ? (
        <Activate
         accessDetails={accessDetails}
         loggedInUserId={userData?.userId}
         actionDetails={actionDetails}
         setLoader={setLoader}
         setActionDetails={handleActionDetailsChange}
        />
       ) : activeTab === 'rejected_user' ? (
        <RejectedUser
         accessDetails={accessDetails}
         userId={userId}
         loggedInUserId={userData?.userId}
         setLoader={setLoader}
         setActionDetails={handleActionDetailsChange}
        />
       ) : (
        <></>
       )}
      </div>
     }
    />
   </div>
  </>
 )
}

export default function UserManagementPageWrapper() {
 // for using different languages and changing the text in to other languages
 // const intl = useIntl()
 const [hidePartent, setHideParent] = useState(false)
 const {t} = useTranslation()
 return (
  <div className='container'>
   <div className='px-[72px] pt-6 pb-[200px] bg-white'>
    {!hidePartent && (
     <PageTitle
      breadcrumbContainerClass={
       'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b'
      }
      breadcrumbs={[
       {
        title: t('breadCrumbs.home'),
        path: '/home-page',
        isActive: false,
       },
       {
        title: t('breadCrumbs.userManagement'),
        path: '',
        isActive: true,
       },
      ]}
      description='Welcome'
     >
      {t('breadCrumbs.home')}
     </PageTitle>
    )}
    <UserManagementPage
     hidePartent={hidePartent}
     setHideParent={setHideParent}
    />
   </div>
  </div>
 )
}
